import NavIconBell from "@/assets/images/icons/nav-icon-bell.svg";
import ExternalLink from "@/components/ExternalLink";
import { Popover } from "@/components/ui/Popover";
import { Sheet, SheetDragHandle, SheetScrollView } from "@/components/ui/Sheet";
import { TruncatedTextReadMore } from "@/components/ui/TruncatedTextReadMore";
import {
  announcementsHasUnreadBell,
  useAnnouncements,
} from "@/hooks/useAnnouncements";
import { useAuth } from "@/lib/AuthContext";
import { queryKeys } from "@/lib/queryClient";
import { useDrawer } from "@/lib/DrawerContext";
import { resolveAppLink } from "@/lib/resolveAppLink";
import { useIsMobile } from "@/lib/useDeviceType";
import {
  markAllAnnouncementsRead,
  markAnnouncementRead,
  markAnnouncementsShown,
} from "@/services/announcements.service";
import { mixpanelService } from "@/services/mixpanel.service";
import type { UserAnnouncementRow } from "@/types/announcements";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname } from "expo-router";
import React, { memo, useCallback, useMemo, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView as RNScrollView,
  View,
} from "react-native";
import { Pressable as GHPressable } from "react-native-gesture-handler";
import { Circle, Image, SizableText, useTheme, XStack, YStack } from "tamagui";
import { BodyText, CardTitle } from "@/components/ui/Typography";

type Props = {
  userId: string;
  iconSlotSize?: number;
  /** Controlled sheet (rendered outside header, e.g. MobileDrawer). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Sheet only — no bell trigger. */
  hideTrigger?: boolean;
};

function startOfLocalDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatPublishedLabel(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const dayMs = 86_400_000;
  const d0 = startOfLocalDay(d);
  const n0 = startOfLocalDay(now);
  const diff = (n0 - d0) / dayMs;
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function rowThumbnail(row: UserAnnouncementRow): string | null {
  const t = row.thumbnail_url?.trim();
  if (t) return t;
  const a = row.instructor_avatar?.trim();
  return a || null;
}

/** Unread rows are beige, so the default cool-gray `$border` (#E5E7EB) nearly
 *  vanishes against them (~1.06:1). Use a warm tan divider instead (~1.34:1). */
const UNREAD_ROW_BG = "#F3EDE4";
const UNREAD_ROW_DIVIDER = "#D8CEC0";

const SUMMARY_TEXT_PROPS = {
  color: "$neutral1000",
  fontFamily: "$body",
  fontSize: 14,
  lineHeight: 20,
  fontWeight: "400",
  letterSpacing: 0,
} as const;

const PANEL_SHELL_SHADOW =
  Platform.OS === "web"
    ? ({
        boxShadow:
          "0 4px 8px 0 rgba(0, 0, 0, 0.10), 0 0 4px 0 rgba(0, 0, 0, 0.08)",
      } as const)
    : {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 6,
      };

const LIST_SCROLL_CONTENT_STYLE = { flexGrow: 0 as const, paddingBottom: 0 };

type AnnouncementRowProps = {
  row: UserAnnouncementRow;
  onTrackClick: (row: UserAnnouncementRow) => void;
  onClose: () => void;
};

function AnnouncementCtaButton({ label }: { label: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <View
      {...(Platform.OS === "web"
        ? ({
            onMouseEnter: () => setHovered(true),
            onMouseLeave: () => setHovered(false),
          } as any)
        : {})}
      style={{
        borderRadius: 30,
        borderWidth: 1,
        borderColor: hovered ? "#0D0D0D" : "#C9C5C3",
        backgroundColor: hovered ? "#0D0D0D" : "#FFF",
        paddingHorizontal: 14,
        paddingVertical: 6,
      }}
    >
      <SizableText
        color={hovered ? "#FFF" : "#0D0D0D"}
        fontFamily="$body"
        fontSize={12}
        lineHeight={20}
        fontWeight="600"
        letterSpacing={0.6}
      >
        {label}
      </SizableText>
    </View>
  );
}

const AnnouncementRow = memo(function AnnouncementRow({
  row,
  onTrackClick,
  onClose,
}: AnnouncementRowProps) {
  const thumb = rowThumbnail(row);
  const read = row.is_read;
  const summary = row.summary?.trim();
  const ctaLabel = row.cta_label?.trim() || null;
  const { href: url } = resolveAppLink(row.link);

  const rowContent = (
    <XStack
      width="100%"
      flexShrink={0}
      alignSelf="stretch"
      minHeight={136}
      borderBottomWidth={1}
      borderBottomColor={read ? "$border" : UNREAD_ROW_DIVIDER}
      bg={read ? "$white" : UNREAD_ROW_BG}
      pt="$4"
      pb="$4"
      px="$6"
      gap="$3"
      ai="flex-start"
      jc="flex-start"
      cursor="pointer"
      {...(Platform.OS === "web"
        ? ({
            onClickCapture: (e: React.MouseEvent) => {
              if (
                (e.target as HTMLElement).closest?.(
                  "[data-announcement-read-more]",
                )
              )
                return;
              if (e.metaKey || e.ctrlKey || e.shiftKey) void onTrackClick(row);
            },
          } as any)
        : {})}
    >
      <YStack
        width={51.2}
        height={51.2}
        borderRadius={25.6}
        overflow="hidden"
        backgroundColor="rgba(0,0,0,0.06)"
        mt={6}
        flexShrink={0}
        ai="center"
        jc="center"
      >
        {thumb ? (
          <Image
            source={{ uri: thumb }}
            width={51.2}
            height={51.2}
            resizeMode="cover"
          />
        ) : null}
      </YStack>
      <YStack
        f={1}
        minWidth={0}
        flexGrow={1}
        flexShrink={1}
        justifyContent="flex-start"
        gap={10}
      >
        <YStack minWidth={0} gap="$1">
          <CardTitle
            color="$neutral1000"
            fontWeight="600"
            letterSpacing={0}
          >
            {row.title}
          </CardTitle>
          {summary ? (
            <TruncatedTextReadMore
              numberOfLines={2}
              textProps={SUMMARY_TEXT_PROPS}
            >
              {summary}
            </TruncatedTextReadMore>
          ) : null}
        </YStack>
        <XStack width="100%" ai="center" jc="space-between" gap="$2">
          {row.published_at ? (
            <BodyText
              color="$neutral600"
              letterSpacing={0}
            >
              {formatPublishedLabel(row.published_at)}
            </BodyText>
          ) : (
            <View />
          )}
          {!read && ctaLabel ? (
            <AnnouncementCtaButton label={ctaLabel} />
          ) : null}
        </XStack>
      </YStack>
      {!read ? (
        <YStack width={10} minWidth={10} pt="$2" jc="flex-start" ai="center">
          <Circle size={10} bg="$accent" />
        </YStack>
      ) : null}
    </XStack>
  );

  return (
    <ExternalLink
      url={url}
      onPress={() => {
        void onTrackClick(row);
        onClose();
      }}
      deferNavigation
    >
      {rowContent}
    </ExternalLink>
  );
});

export function AnnouncementsDropdown({
  userId,
  iconSlotSize = 36,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { openAnnouncements, setAnnouncementsOpen } = useDrawer();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const pathname = usePathname();
  const [modalOpen, setModalOpen] = useState(false);
  const [sheetOpenState, setSheetOpenState] = useState(false);

  const sheetOpen = controlledOpen ?? sheetOpenState;
  const setSheetOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) setSheetOpenState(open);
      onOpenChange?.(open);
      if (!hideTrigger) setAnnouncementsOpen(open);
    },
    [controlledOpen, hideTrigger, onOpenChange, setAnnouncementsOpen],
  );

  const {
    data: rows = [],
    isPending,
    isError,
    refetch,
    isRefetching,
  } = useAnnouncements({
    userId,
    enabled: true,
  });

  const hasUnreadBell = announcementsHasUnreadBell(rows);

  const closePanels = useCallback(() => {
    setModalOpen(false);
    setSheetOpen(false);
  }, []);

  const onPanelOpened = useCallback(async () => {
    try {
      const { data: freshRows, isError } = await refetch();
      if (isError) return;
      const next = freshRows ?? [];
      if (!announcementsHasUnreadBell(next)) return;
      const ok = await markAnnouncementsShown();
      if (!ok) return;
      queryClient.setQueryData<UserAnnouncementRow[]>(
        queryKeys.announcements(userId),
        (prev) => (prev ?? []).map((r) => ({ ...r, has_unread_bell: false })),
      );
    } catch {}
  }, [queryClient, refetch, userId]);

  const handleSheetOpen = useCallback(() => {
    openAnnouncements();
    void onPanelOpened();
  }, [onPanelOpened, openAnnouncements]);

  const onTrackClick = useCallback(
    async (row: UserAnnouncementRow) => {
      await mixpanelService.trackAnnouncementClick(
        row.id,
        row.title?.trim() ?? "",
        resolveAppLink(row.link).href,
        user,
        pathname,
      );
      await mixpanelService.flush();
      if (!row.is_read) {
        queryClient.setQueryData<UserAnnouncementRow[]>(
          queryKeys.announcements(userId),
          (prev) =>
            (prev ?? []).map((p) =>
              p.id === row.id ? { ...p, is_read: true } : p,
            ),
        );
        void markAnnouncementRead(String(row.id));
      }
    },
    [pathname, queryClient, user, userId],
  );

  const onMarkAllRead = useCallback(async () => {
    const ok = await markAllAnnouncementsRead();
    if (ok) {
      queryClient.setQueryData<UserAnnouncementRow[]>(
        queryKeys.announcements(userId),
        (prev) => (prev ?? []).map((p) => ({ ...p, is_read: true })),
      );
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.announcements(userId),
    });
  }, [queryClient, userId]);

  const isWeb = Platform.OS === "web";
  const [navHover, setNavHover] = useState(false);
  const iconColor =
    isWeb && navHover
      ? String(theme.accent?.val ?? theme.accent)
      : String(theme.white?.val ?? "#FFFFFF");

  const listLoading = (isPending || isRefetching) && rows.length === 0;
  const showError = isError;
  const hasUnreadInList = useMemo(() => rows.some((r) => !r.is_read), [rows]);

  const renderHeader = (forSheet: boolean) => {
    if (forSheet) {
      if (rows.length === 0) return null;
      return (
        <YStack
          w="100%"
          bg="$white"
          py="$2"
          px="$3"
          ai="center"
          jc="center"
          borderBottomWidth={1}
          borderBottomColor="$border"
        >
          {hasUnreadInList ? (
            <GHPressable onPress={() => void onMarkAllRead()} hitSlop={12}>
              <BodyText
                ta="center"
                color="#795C40"
                fontWeight="500"
                letterSpacing={0}
              >
                Mark All as Read
              </BodyText>
            </GHPressable>
          ) : (
            <BodyText
              ta="center"
              color="rgba(13, 13, 13, 0.45)"
              fontWeight="500"
              letterSpacing={0}
            >
              All read
            </BodyText>
          )}
        </YStack>
      );
    }
    return (
      <XStack
        jc="space-between"
        ai="center"
        w="100%"
        bg="$white"
        px="$6"
        py="$3"
        borderBottomWidth={1}
        borderBottomColor="$border"
      >
        <XStack f={1} minWidth={0} ai="center" jc="flex-start">
          {rows.length > 0 && hasUnreadInList ? (
            <Pressable onPress={() => void onMarkAllRead()} hitSlop={6}>
              <BodyText
                color="#795C40"
                fontWeight="500"
                letterSpacing={0}
              >
                Mark All as Read
              </BodyText>
            </Pressable>
          ) : rows.length > 0 ? (
            <BodyText
              color="rgba(13, 13, 13, 0.45)"
              fontWeight="500"
              letterSpacing={0}
            >
              All read
            </BodyText>
          ) : null}
        </XStack>
        <Pressable onPress={closePanels} hitSlop={6}>
          <BodyText
            color="$darkBase"
            fontWeight="500"
            letterSpacing={0}
          >
            Close
          </BodyText>
        </Pressable>
      </XStack>
    );
  };

  const renderPanel = (forSheet: boolean) => {
    const sheetBg = String(theme.white?.val ?? "#FFFFFF");
    const listScrollStyle = forSheet
      ? ({ flex: 1, minHeight: 0, backgroundColor: sheetBg } as const)
      : ({ maxHeight: 420, backgroundColor: sheetBg } as const);

    const body = showError ? (
      <YStack
        f={forSheet ? 1 : undefined}
        minHeight={forSheet ? 0 : 120}
        px="$5"
        py="$6"
        ai="center"
        jc="center"
        bg="white"
      >
        <BodyText color="$muted" ta="center">
          We couldn’t load announcements. Try again later.
        </BodyText>
      </YStack>
    ) : listLoading ? (
      <YStack
        f={forSheet ? 1 : undefined}
        minHeight={forSheet ? 0 : undefined}
        px="$5"
        py="$8"
        ai="center"
        jc="center"
        bg="white"
      >
        <BodyText color="$muted">
          Loading…
        </BodyText>
      </YStack>
    ) : rows.length === 0 ? (
      <YStack
        f={forSheet ? 1 : undefined}
        minHeight={forSheet ? 0 : undefined}
        px="$5"
        py="$8"
        ai="center"
        jc="center"
        bg="white"
      >
        <BodyText color="$muted" ta="center">
          You’re all caught up.
        </BodyText>
      </YStack>
    ) : (
      React.createElement(
        forSheet ? SheetScrollView : RNScrollView,
        {
          style: listScrollStyle,
          contentContainerStyle: LIST_SCROLL_CONTENT_STYLE,
          showsVerticalScrollIndicator: !forSheet,
          bounces: false,
          contentInsetAdjustmentBehavior: "never" as const,
        },
        <YStack w="100%" bg="$white">
          {rows.map((row) => (
            <AnnouncementRow
              key={String(row.id)}
              row={row}
              onTrackClick={onTrackClick}
              onClose={closePanels}
            />
          ))}
        </YStack>,
      )
    );

    return (
      <YStack
        w="100%"
        maw={forSheet ? undefined : 558}
        f={forSheet ? 1 : undefined}
        minHeight={forSheet ? 0 : undefined}
        bg="$white"
        br={forSheet ? 0 : "$4"}
        overflow="hidden"
        style={forSheet ? undefined : PANEL_SHELL_SHADOW}
      >
        {renderHeader(forSheet)}
        {body}
      </YStack>
    );
  };

  const triggerInner = (
    <XStack
      position="relative"
      width={iconSlotSize}
      height={iconSlotSize}
      ai="center"
      jc="center"
      cursor="pointer"
    >
      <NavIconBell
        width={iconSlotSize}
        height={iconSlotSize}
        color={iconColor}
      />
      {hasUnreadBell ? (
        <Circle size={9} position="absolute" top={3} right={2} bg="$error" />
      ) : null}
    </XStack>
  );

  const triggerWrap = isWeb ? (
    <div
      style={{
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 2,
        paddingRight: 2,
        borderRadius: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseEnter={() => setNavHover(true)}
      onMouseLeave={() => setNavHover(false)}
    >
      {triggerInner}
    </div>
  ) : (
    <View
      style={{
        borderRadius: 6,
        paddingVertical: 2,
        paddingHorizontal: 2,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {triggerInner}
    </View>
  );

  const sheetNode = (
    <Sheet
      open={isMobile && sheetOpen}
      onOpenChange={(open) => {
        if (open === sheetOpen) return;
        setSheetOpen(open);
        if (open) setModalOpen(false);
      }}
      snapPoints={[80, 50]}
      backgroundColor={String(theme.white?.val ?? "#FFFFFF")}
    >
      <YStack f={1} w="100%" minHeight={0} backgroundColor="white">
        <SheetDragHandle />
        {renderPanel(true)}
      </YStack>
    </Sheet>
  );

  if (hideTrigger) {
    return sheetNode;
  }

  return (
    <>
      <Popover
        open={!isMobile && modalOpen}
        onOpenChange={(open) => {
          if (open === modalOpen) return;
          setModalOpen(open);
          if (open) {
            void onPanelOpened();
          }
        }}
        onMobileOpen={handleSheetOpen}
        placement="bottom-end"
        offset={30}
        width={558}
        maxWidth={558}
        maxHeight={520}
        contentBackgroundColor="transparent"
        bareContent
        content={renderPanel(false)}
      >
        {triggerWrap}
      </Popover>
    </>
  );
}
